import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { LANG_LABELS, SUPPORTED_LANGS, type Lang } from "@/mocks/site";

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = (i18n.language?.split("-")[0] as Lang) || "en";

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const switchLang = (code: Lang) => {
    setOpen(false);
    i18n.changeLanguage(code);
    const parts = location.pathname.split("/").filter(Boolean);
    parts[0] = code;
    navigate(`/${parts.join("/")}`);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-foreground-900"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <i className="ri-global-line text-base text-foreground-500" />
        <span>{LANG_LABELS[current]}</span>
        <i className={`ri-arrow-down-s-line text-sm transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <ul
          className="absolute right-0 z-50 mt-2 w-40 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1 text-sm"
          role="listbox"
        >
          {SUPPORTED_LANGS.map((code) => (
            <li key={code}>
              <button
                type="button"
                onClick={() => switchLang(code)}
                className={`flex w-full items-center gap-2 whitespace-nowrap px-4 py-2 text-left transition-colors hover:bg-background-100 ${
                  current === code ? "font-semibold text-primary-700" : "text-foreground-700"
                }`}
                role="option"
                aria-selected={current === code}
              >
                {current === code && <i className="ri-check-line text-primary-500" />}
                <span className={current !== code ? "ml-[18px]" : ""}>{LANG_LABELS[code]}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}